import {onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {CHART_MAX_VALUE, CHART_MIN_VALUE} from './radialChart';

/** Controls how long it takes (in ms) to animate 1 full unit of value. */
const PER_UNIT_DURATION_MS = 35;

/** Minimum duration (in ms) for any animation, even for very small value changes. */
const MIN_ANIMATION_DURATION_MS = 200;

/** Duration (in ms) of the centre label fade-in at the start. */
export const CENTRE_FADE_DURATION_MS = 800;

/** Pause (in ms) when the animated value hits the 33 and 66 thresholds. */
const THRESHOLD_PAUSE_MS = 30;

/** Delay (in ms) after the main animation completes before post effects start fading in. */
const POST_ANIMATION_FADE_IN_DELAY_MS = 300;

/** Duration (in ms) that post-animation elements (marker, legend) use for their opacity transition. */
export const POST_ANIMATION_FADE_IN_DURATION_MS = 800;

/** Shared animation state — module-level so all consumers see the same values. */
export const currentValue = ref(0);
export const centreVisible = ref(false);
export const hasStartedAnimation = ref(false);
export const startPostAnimationEffects = ref(false);

let animationFrameId = null;
let pauseTimeoutId = null;
let postAnimationTimeoutId = null;

/** Central composable: animates the value, handles initial centre fade-in, and signals when post-animation effects should start. */
export const useRadialAnimation = (getTargetValue, getIsLoading = () => false) => {

  const resetAnimationState = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    if (pauseTimeoutId !== null) {
      window.clearTimeout(pauseTimeoutId);
      pauseTimeoutId = null;
    }
    if (postAnimationTimeoutId !== null) {
      window.clearTimeout(postAnimationTimeoutId);
      postAnimationTimeoutId = null;
    }
    startPostAnimationEffects.value = false;
  };

  const getSafeTargetValue = () => {
    const raw = getTargetValue();

    if (typeof raw !== 'number' || Number.isNaN(raw)) {
      return null;
    }

    return raw;
  };

  /** Animate currentValue from startValue to endValue with easing, then invoke the optional onComplete callback. */
  const animateRange = (startValue, endValue, onComplete) => {
    if (startValue === endValue) {
      currentValue.value = startValue;
      if (onComplete) {
        onComplete();
      }
      return;
    }

    const valueDelta = endValue - startValue;
    const distance = Math.abs(Math.round(valueDelta));
    const animationDuration = Math.max(
      MIN_ANIMATION_DURATION_MS,
      distance * PER_UNIT_DURATION_MS,
    );
    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const linearProgress = Math.min(1, elapsed / animationDuration);

      // Ease-in-out cubic: slow at start, speeds up, then slows near the end.
      const easedProgress =
        linearProgress < 0.5
          ? 4 * linearProgress * linearProgress * linearProgress
          : 1 - Math.pow(-2 * linearProgress + 2, 3) / 2;

      currentValue.value = Math.round(
        (startValue + valueDelta * easedProgress) * 100,
      ) / 100;

      if (linearProgress < 1) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        animationFrameId = null;
        currentValue.value = endValue;
        if (onComplete) {
          onComplete();
        }
      }
    };

    animationFrameId = requestAnimationFrame(step);
  };

  /** Orchestrate a full animation to targetValue, including threshold pauses and post-animation effect timing. */
  const animateToValue = (targetValue) => {
    if (getIsLoading()) {
      return;
    }

    const baseTarget =
      typeof targetValue === 'number' && !Number.isNaN(targetValue)
        ? targetValue
        : getSafeTargetValue();

    if (baseTarget === null) {
      resetAnimationState();
      return;
    }

    const clampedTarget = Math.min(
      CHART_MAX_VALUE,
      Math.max(CHART_MIN_VALUE, baseTarget),
    );

    if (clampedTarget === currentValue.value) {
      return;
    }

    resetAnimationState();

    const startValue = currentValue.value;

    // If we're decreasing, just animate in one go with no pauses.
    if (clampedTarget <= startValue) {
      animateRange(startValue, clampedTarget);
      return;
    }

    const thresholds = [33.5, 66.5];
    const segments = [startValue];

    thresholds.forEach((threshold) => {
      if (startValue < threshold && clampedTarget >= threshold) {
        segments.push(threshold);
      }
    });

    segments.push(clampedTarget);

    const runSegment = (index) => {
      if (index >= segments.length - 1) {
        return;
      }

      const from = segments[index];
      const to = segments[index + 1];
      const isFinalSegment = to === clampedTarget;

      animateRange(from, to, () => {
        if (isFinalSegment) {
          // Delay before we reveal any post-animation elements (e.g. markers, legend).
          postAnimationTimeoutId = window.setTimeout(() => {
            postAnimationTimeoutId = null;
            startPostAnimationEffects.value = true;
          }, POST_ANIMATION_FADE_IN_DELAY_MS);
          return;
        }

        if ((to === 33.5 || to === 66.5) && to !== clampedTarget) {
          pauseTimeoutId = window.setTimeout(() => {
            pauseTimeoutId = null;
            runSegment(index + 1);
          }, THRESHOLD_PAUSE_MS);
        } else {
          runSegment(index + 1);
        }
      });
    };

    runSegment(0);
  };

  watch(
    () => getTargetValue(),
    (newValue) => {
      if (!hasStartedAnimation.value) {
        return;
      }

      if (getIsLoading()) {
        return;
      }

      animateToValue(newValue);
    },
    { immediate: false },
  );

  watch(
    () => getIsLoading(),
    (isLoading) => {
      if (!hasStartedAnimation.value) {
        return;
      }

      if (isLoading) {
        resetAnimationState();
        return;
      }

      animateToValue(getTargetValue());
    },
    { immediate: false },
  );

  onMounted(() => {
    window.setTimeout(() => {
      hasStartedAnimation.value = true;
      if (!getIsLoading()) {
        animateToValue(getTargetValue());
      }
    }, CENTRE_FADE_DURATION_MS);
  });

  onBeforeUnmount(() => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }
    if (pauseTimeoutId !== null) {
      window.clearTimeout(pauseTimeoutId);
    }
    if (postAnimationTimeoutId !== null) {
      window.clearTimeout(postAnimationTimeoutId);
    }

  });

  return {
    currentValue,
    centreVisible,
    startPostAnimationEffects,
  };
};

export const postAnimationEffectsCompleted = (getStartPostAnimationEffects) => {
  const completed = ref(false);

  watch(getStartPostAnimationEffects, (val) => {
    if (val) {
      window.setTimeout(() => { completed.value = true; }, POST_ANIMATION_FADE_IN_DURATION_MS);
    } else {
      completed.value = false;
    }
  });

  return completed;
};

